'use client';

//import {SiteConfig} from '@data/types';
import { Logo } from '@components/Logo';
import RichText from '@components/RichText';
import SocialSubscribing, {
  SocialSubscribingProps,
} from '@components/SocialMediaLinks/SocialSubscribing';
import { ExternalLink } from '@icon/index';
import { getPhoneFormattedText } from '@util/phoneNumber';
import { useParams } from 'next/navigation';
import { FC } from 'react';
import { Accordion } from './Accordion';
import { FooterPattern } from './FooterPattern';
//styling
import { getFooterTestId, getTestId } from '@util/getTestIds';
import { PH_ENABLE_SHOP_DEVELOPMENTS } from 'app/PostHogProvider';
import { MessageKeys, useTranslations } from 'next-intl';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  AdditionalInformationWrapper,
  AddressInformationWrapper,
  CompanyInformationWrapper,
  FooterInner,
  LogoSizer,
  PatternWrapper,
  SocialWrapper,
  StyledBold,
  StyledCity,
  StyledColumnContainer,
  StyledDesktopContainer,
  StyledLegal,
  StyledMiddleContainer,
  StyledMobile,
  StyledRegular,
  StyledRegularLink,
  StyledRegularLinkItem,
  StyledTopContainer,
} from './styles';
import isObjectEmpty from '@util/isObjectEmpty';
import { getHostWithProtocol } from '@util/blog';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';

interface FooterProps {
  siteConfig: any;
  shopFooterData?: any;
  shopData?: any;
  hasDevServicesPage?: boolean;
}

//Social Media Icons and links
const MappedGLOBALSocialSubscribing = (block) => {
  //create an array of social media objects according to SocialMedia interface
  const socialMedias = block.props.map((socialMedia) => {
    return {
      platform: socialMedia?.fields.text.toLowerCase(),
      url: socialMedia?.fields.url,
    };
  });
  const props: SocialSubscribingProps = {
    socialMedias: socialMedias,
  };
  return <SocialSubscribing {...props} />;
};

//Social Media Icons and links
const MappedSocialSubscribing = ({ socialMedias }) => {
  const props: SocialSubscribingProps = {
    socialMedias: socialMedias,
  };
  return <SocialSubscribing {...props} />;
};

export const Footer: FC<FooterProps> = observer(
  ({ siteConfig, shopFooterData, shopData, hasDevServicesPage }) => {
    const { searchStore } = useStores();
    const { frequentlySearchedAreas } = searchStore;
    const t = useTranslations();
    const params = useParams();
    const enableShopDevelopments = useFeatureFlagEnabled(PH_ENABLE_SHOP_DEVELOPMENTS);
    const siteWideLegal = siteConfig?.bodyRichText;

    //
    //
    // Link Columns
    //
    let footerColumnSource = shopFooterData ? shopFooterData : siteConfig;

    const columnArr = [
      footerColumnSource?.columnOne,
      footerColumnSource?.columnTwo,
      footerColumnSource?.columnThree,
      footerColumnSource?.columnFour,
      footerColumnSource?.columnFive,
      footerColumnSource?.columnSix,
    ].filter(Boolean);
    type footerCol = any;

    const sortOrder: Record<string, number> = {
      neighborhood: 1,
      city: 2,
      city_region: 3,
      state: 4,
      country: 5,
    };

    const categoryMap = {
      neighborhood: 'Neighborhood',
      city: 'City',
      city_region: 'City Region',
      state: 'State/Province',
      country: 'Country',
    };

    if (frequentlySearchedAreas && !isObjectEmpty(frequentlySearchedAreas)) {
      const frequentlySearchedAreasColumn: Record<string, any>[] = [];
      frequentlySearchedAreasColumn.push({
        fields: {
          text: t('Frequently Searched Areas'),
          fields: false,
        },
        metadata: {
          type: 'FSA',
          locationType: null,
        },
        sys: {},
      });
      Object.entries(frequentlySearchedAreas)
        .sort(([a], [b]) => (sortOrder[a] || 99) - (sortOrder[b] || 99))
        .forEach((fsa) => {
          const parsedFsa: Record<string, any> = JSON.parse(fsa[1]);
          const query: Record<string, string> = {
            ...parsedFsa.addressComponents,
            location: parsedFsa.text,
          };
          // :any Needed to circumvent weird typing error with useTranslations
          const locationType: any = categoryMap[fsa[0]];
          const text = t(locationType) + ': ' + parsedFsa.text;
          frequentlySearchedAreasColumn.push({
            fields: {
              external: true,
              text,
              url: parsedFsa.listingUrl
                ? `${parsedFsa.listingUrl}`
                : `properties?${new URLSearchParams(query).toString()}`,
            },
            metadata: {
              type: 'FSA',
              locationType,
            },
            sys: {},
          });
        });

      columnArr.push(frequentlySearchedAreasColumn);
    }

    // Created ColumnData Function to Prevent
    // Styled Systems Styling Repetition
    const columnData = (colArr: footerCol[]) => {
      return colArr.map((col, colIndex) => (
        <div key={colIndex}>
          {col.map((link, linkIndex) => {
            let url = link.fields.external ? link.fields.url : `/${link.fields.url}`;

            const formatLinkForDevelopments =
              hasDevServicesPage &&
              enableShopDevelopments &&
              url?.includes('explore/development-services');
            const shopname = params?.shopname;
            const locale = (params?.lang as string) ?? 'en';
            if (
              url &&
              url.startsWith('/') &&
              locale != 'en' &&
              locale.toLocaleLowerCase() != 'en-us'
            )
              url = `/${locale}${url}`;

            if (formatLinkForDevelopments) {
              url = url.replace('https://www.', `https://${shopname}.`);
            }
            if (link.metadata?.type === 'FSA' && link.metadata.locationType !== null) {
              url = `/${link.fields.url}`;
            }

            return linkIndex === 0 ? (
              <StyledBold
                key={`${colIndex}-${linkIndex}`}
                data-testid={getFooterTestId(link.fields.text, linkIndex)}
              >
                {link.fields.text}
              </StyledBold>
            ) : (
              <StyledRegularLinkItem
                data-testid={`${getTestId(link.fields.text)}-footer`}
                key={linkIndex}
              >
                <StyledRegularLink
                  key={`${colIndex}-${linkIndex}`}
                  href={url}
                  data-testid={getFooterTestId(link.fields.text, linkIndex)}
                  target={link.fields.external && !formatLinkForDevelopments ? '_blank' : ''}
                >
                  {link.fields.text}
                  {link.fields.external && !formatLinkForDevelopments && <ExternalLink size="16" />}
                </StyledRegularLink>
              </StyledRegularLinkItem>
            );
          })}
        </div>
      ));
    };

    //
    //
    // Add Current Date to Legal
    //
    let addDateToLegal = (richtext) => {
      if (richtext?.content?.[0]?.content?.[0]) {
        const firstParagraph = richtext.content[0].content[0];
        const currentYear = new Date().getFullYear();
        const existingValue = firstParagraph.value;

        if (!existingValue?.includes(`©2006-${currentYear}`)) {
          const newValue = `©2006-${currentYear} ${existingValue}`;
          firstParagraph.value = newValue;
        }
      }
      return richtext;
    };

    //
    //
    // Social Media prep
    //
    const socialMediaLinks = [
      // do not change the order. this order matches the design of the site
      { platform: 'instagram', url: shopData?.urlInstagram },
      { platform: 'twitter', url: shopData?.urlTwitter },
      { platform: 'facebook', url: shopData?.urlFacebook },
      { platform: 'linkedin', url: shopData?.urlLinkedin },
      { platform: 'youtube', url: shopData?.urlYoutube },
      { platform: 'tiktok', url: shopData?.urlTiktok },
    ].filter((socialMedia) => socialMedia.url);

    //
    //
    // Name and Address data
    //
    let name;
    let address;
    let postalCode;
    let cityStateZip;
    if (shopData) {
      //Name processing
      const contentfulNameOverwrite = shopFooterData?.name;
      name = contentfulNameOverwrite
        ? contentfulNameOverwrite
        : shopData?.name
          ? shopData?.name
          : null;
      //Address processing
      const contentfulAddressOverwrite = shopFooterData?.address;
      const apiAddress = shopData?.address;
      address = apiAddress
        ? apiAddress
        : contentfulAddressOverwrite
          ? contentfulAddressOverwrite
          : null;

      // TEMP: Remove contentful logic for postal codes when they are added in api data for all shop groups
      const splitShopFooterContentfulAddress = shopFooterData?.address
        ? shopFooterData?.address?.split(',')
        : [];

      const lastPartOfContentfulAddress =
        splitShopFooterContentfulAddress?.length > 0 &&
        splitShopFooterContentfulAddress[splitShopFooterContentfulAddress?.length - 1];

      const postalCodeFromContentfulAddress =
        lastPartOfContentfulAddress &&
        !isNaN(lastPartOfContentfulAddress) &&
        lastPartOfContentfulAddress?.trim().length === 5
          ? lastPartOfContentfulAddress
          : '';

      postalCode = shopData?.postalCode || postalCodeFromContentfulAddress || '';
    } else {
      // siteConfig comes from Site Footer in contentful
      name = siteConfig?.name;
      address = siteConfig?.address;
      cityStateZip = siteConfig?.cityStateZip;
    }
    const formattedPhoneNumber = shopData?.phone
      ? getPhoneFormattedText(shopData.phone, shopData.country)
      : null;

    return (
      <footer>
        <FooterInner data-testid="footer-section">
          <StyledTopContainer>
            <CompanyInformationWrapper>
              <LogoSizer>
                <Logo size="100%" dataTestId="footer-logo" />
              </LogoSizer>

              {shopData && (
                <>
                  <AddressInformationWrapper>
                    {name && (
                      <div>
                        <StyledCity data-testid="footer-city" style={{ marginBottom: '1.5rem' }}>
                          {name}
                        </StyledCity>
                      </div>
                    )}
                    {shopData.street1 ? (
                      <div>
                        <StyledRegular data-testid="shop-footer-street1">
                          {shopData.street1}
                        </StyledRegular>
                      </div>
                    ) : (
                      address && (
                        <div>
                          <StyledRegular data-testid="footer-address">{address}</StyledRegular>
                          {cityStateZip && (
                            <div>
                              <StyledRegular data-testid="footer-city-state-zip">
                                {cityStateZip}
                              </StyledRegular>
                            </div>
                          )}
                        </div>
                      )
                    )}
                    {shopData.street2 ? (
                      <div>
                        <StyledRegular data-testid="shop-footer-street2">
                          {shopData.street2}
                        </StyledRegular>
                      </div>
                    ) : (
                      shopData.address2 && (
                        <div>
                          <StyledRegular>{shopData.address2}</StyledRegular>
                        </div>
                      )
                    )}

                    {(shopData.city || shopData.state) && (
                      <div>
                        <StyledRegular data-testid="shop-footer-city-state-zip">{`${
                          (shopData.city && shopData.city + ', ') +
                          (shopData.state && shopData.state + ' ') +
                          (postalCode && postalCode)
                        }`}</StyledRegular>
                      </div>
                    )}

                    {shopData.country && (
                      <div>
                        <StyledRegular data-testid="shop-footer-country">
                          {shopData.country}
                        </StyledRegular>
                      </div>
                    )}
                  </AddressInformationWrapper>

                  <AdditionalInformationWrapper>
                    {formattedPhoneNumber && (
                      <a data-testid="footer-phone" href={`tel:${formattedPhoneNumber}`}>
                        <StyledRegular>{formattedPhoneNumber}</StyledRegular>
                      </a>
                    )}
                    {shopData.licenses?.length > 0 && (
                      <div>
                        <StyledRegular data-testid="footer-license">
                          {t('License #')}:{shopData.licenses[0]?.licenseNumber}
                        </StyledRegular>
                      </div>
                    )}
                  </AdditionalInformationWrapper>

                  {socialMediaLinks && socialMediaLinks.length > 0 && (
                    <>
                      <SocialWrapper data-testid="shop-social-media-links">
                        <MappedSocialSubscribing socialMedias={socialMediaLinks} />
                      </SocialWrapper>
                    </>
                  )}
                </>
              )}

              {!shopData && siteConfig?.svgs && siteConfig.svgs.some((obj) => obj.fields) && (
                <SocialWrapper data-testid="social-media-links">
                  <MappedGLOBALSocialSubscribing props={siteConfig.svgs} />
                </SocialWrapper>
              )}
            </CompanyInformationWrapper>
          </StyledTopContainer>

          {columnArr &&
            !columnArr.every((item) => item === undefined) && // if there are no columns added in Contentful
            columnArr.every((item) => item.every((element) => element.fields)) && ( // also check if every element in columnArr has a fields property (it will not have the fields property if it's a Contentful draft)
              <StyledMiddleContainer>
                <StyledDesktopContainer>
                  <StyledColumnContainer>{columnData(columnArr)}</StyledColumnContainer>
                </StyledDesktopContainer>
                <StyledMobile>
                  <Accordion content={columnArr} />
                </StyledMobile>
              </StyledMiddleContainer>
            )}

          <StyledLegal data-testid="footer-legal">
            {shopFooterData?.richText /* Optional Shops Rich Text */ && (
              <>
                <RichText richText={shopFooterData.richText} />
              </>
            )}

            <RichText richText={addDateToLegal(siteWideLegal)} />
            {/* Global Legal */}
          </StyledLegal>
        </FooterInner>

        <PatternWrapper>
          <FooterPattern />
        </PatternWrapper>
      </footer>
    );
  },
);
